import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useQuery } from 'react-query';
import { ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { CreateFlowModal } from './CreateFlowModal';
import { ModalContent } from './shared/FormComponents';
import { CreateFlowBackButton } from './shared/CreateFlowBackButton';
import { TableSearchBar } from 'components/tables/TableSearchBar';
import { Pagination } from 'components/reports/controls/Pagination';
import { TableBody } from 'components/tables/learner-stats/TableBody';
import { TableHeader } from 'components/tables/learner-stats/TableHeader';
import ImageBadge from 'components/images/ImageBadge';
import LoadingComponent from 'components/LoadingComponent';
import { Color } from 'core';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { normalizeJSONAPIResponse } from 'utils/modelUtils';
import { getErrorStringFromAPIError } from 'utils/errorUtils';
import { getBaseReactUrl } from 'utils/pathUtils';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { AnalyticsService } from 'utils/AnalyticsService';

interface IProps {
  onClose: () => void;
  onBack: () => void;
}

export const FromExistingCourseModal = (props: IProps) => {
  const { onClose, onBack } = props;
  const { t } = useTranslation();
  const user = useCurrentUser();
  const primaryPartnerId = user?.primaryPartner.id;

  const [searchName, setSearchName] = useState<string>('');
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
  const [isCloning, setIsCloning] = useState<boolean>(false);

  const getCoursesForModal = async () => {
    const endpoint = `/api/v4/partners/${primaryPartnerId}/courses`;
    let queryParams = `limited=true&include=image&page[number]=${pageNumber}&page[size]=${pageSize}&status_filter=all&sort=-last_updated`;
    if (searchName) {
      queryParams += `&course_name=${searchName}`;
    }
    const response = await axios.get(`${endpoint}?${queryParams}`);
    const normalizedData = normalizeJSONAPIResponse(response.data);
    return {
      data: normalizedData,
      meta: response.data.meta,
    };
  };

  const { data, status: queryStatus } = useQuery(
    ['from-existing-courses', primaryPartnerId, pageNumber, pageSize, searchName],
    getCoursesForModal,
    {
      enabled: !!primaryPartnerId,
      keepPreviousData: true,
    }
  );

  const courses = data?.data || [];
  const coursesMeta = data?.meta;

  const totalCount = coursesMeta ? coursesMeta['total-count'] : 0;
  const totalPages = coursesMeta ? coursesMeta['total-pages'] : 0;

  useEffect(() => {
    setPageNumber(1);
  }, [searchName]);

  const goToPageOne = () => setPageNumber(1);
  const goToNextPage = () => pageNumber < totalPages && setPageNumber(pageNumber + 1);
  const goToPreviousPage = () => pageNumber > 1 && setPageNumber(pageNumber - 1);
  const goToLastPage = () => setPageNumber(totalPages);

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPageNumber(1);
  };

  const handleRowClick = (courseId: string) => {
    setSelectedCourseId(courseId === selectedCourseId ? null : courseId);
  };

  const handleCreateCopy = async () => {
    if (!selectedCourseId) return;

    setIsCloning(true);
    try {
      const response = await axios.post(`/api/v3/courses/${selectedCourseId}/clone`, {
        with_assignments: true,
        with_goals: true,
        with_release_date: true,
        with_due_date: true,
      });

      const newCourse = normalizeJSONAPIResponse(response.data);

      AnalyticsService.getInstance().track('creation_completed', {
        creation_type: 'course',
        creation_method: 'from_existing',
      });

      window.location.href = `${getBaseReactUrl()}/app/nav/courses/${newCourse.id}`;
    } catch (error) {
      setIsCloning(false);
      alert(`Error cloning: ${getErrorStringFromAPIError(error)}`);
    }
  };

  const columns: ColumnDef<any>[] = [
    {
      id: 'name',
      accessorFn: (row) => row.name,
      cell: ({ row }) => {
        const rowData = row.original;
        const isSelected = selectedCourseId === rowData.id.toString();
        return (
          <SelectableRow
            isSelected={isSelected}
            onClick={() => handleRowClick(rowData.id.toString())}
          >
            <RadioCircle isSelected={isSelected} />
            <CourseImageContainer>
              <ImageBadge image={rowData.image} style={courseImageStyles} entity="course" />
            </CourseImageContainer>
            <CourseNameContainer>
              <CourseName>{rowData.name}</CourseName>
              <InstructorNames>{rowData.instructorNames?.join(', ') || t('No Instructors Yet')}</InstructorNames>
            </CourseNameContainer>
          </SelectableRow>
        );
      },
      header: () => <TableHeaderText>{t('Course')}</TableHeaderText>,
    },
    {
      id: 'learners',
      accessorFn: (row) => row.studentUsersCount,
      cell: ({ row }) => <CenteredCell>{row.original.studentUsersCount || 0}</CenteredCell>,
      header: () => <TableHeaderText>{t('Learners')}</TableHeaderText>,
    },
    {
      id: 'assignments',
      accessorFn: (row) => row.goalsCount,
      cell: ({ row }) => <CenteredCell>{row.original.goalsCount || 0}</CenteredCell>,
      header: () => <TableHeaderText>{t('Assignments')}</TableHeaderText>,
    },
    {
      id: 'last_updated',
      accessorFn: (row) => row.updatedAt,
      cell: ({ row }) => <CenteredCell>{moment(row.original.updatedAt).format('MM/DD/YY')}</CenteredCell>,
      header: () => <TableHeaderText>{t('Last Updated')}</TableHeaderText>,
    },
  ];

  const table = useReactTable({
    data: courses,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <CreateFlowModal title="Select a course to copy" onClose={onClose}>
      <ModalContent>
        <CreateFlowBackButton onClick={onBack} />

        <ToolbarContainer>
          <TableSearchBar
            value={searchName}
            onChange={setSearchName}
            resetPage={() => {}}
            placeholder={t('Search courses')}
            style={{ width: '100%' }}
          />
        </ToolbarContainer>

        {queryStatus === 'loading' ? (
          <LoadingComponent component={<></>} isLoading={true} />
        ) : (
          <TableContainer>
            <StyledTable>
              <TableHeader table={table} handleHeaderClick={() => {}} />
              <TableBody table={table} />
            </StyledTable>
            {courses.length === 0 && totalCount === 0 && (
              <EmptyState>{t('No courses found')}</EmptyState>
            )}
            {totalCount > 0 && (
              <Pagination
                totalCount={totalCount}
                page={pageNumber}
                pageSize={pageSize}
                goToPageOne={goToPageOne}
                goToPreviousPage={goToPreviousPage}
                goToNextPage={goToNextPage}
                goToLastPage={goToLastPage}
                onPageSizeChange={handlePageSizeChange}
              />
            )}
          </TableContainer>
        )}

        <ButtonContainer>
          <CreateCopyButton
            onClick={handleCreateCopy}
            disabled={!selectedCourseId || isCloning}
          >
            {isCloning ? 'Creating Copy...' : 'Create Copy'}
          </CreateCopyButton>
        </ButtonContainer>
      </ModalContent>
    </CreateFlowModal>
  );
};

const ToolbarContainer = styled.div`
  display: flex;
  border-bottom: 1px solid ${Color.lightGray};
  flex-shrink: 0;
  padding-bottom: 16px;
  margin-bottom: 16px;
`;

const TableContainer = styled.div`
  flex: 1;
  max-height: 400px;
  overflow-y: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeaderText = styled.span`
  font-weight: 600;
  color: ${Color.darkGray};
`;

const SelectableRow = styled.div<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  padding: 8px 0;
  cursor: pointer;
  border-radius: 4px;
  background-color: ${(props) => (props.isSelected ? Color.secondaryBlue : 'transparent')};

  &:hover {
    background-color: ${(props) => (props.isSelected ? Color.secondaryBlue : Color.veryLightGray)};
  }
`;

const RadioCircle = styled.div<{ isSelected: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid ${(props) => (props.isSelected ? Color.primaryBlue : Color.lightGray)};
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &::after {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: ${(props) => (props.isSelected ? Color.primaryBlue : 'transparent')};
  }
`;

const CourseImageContainer = styled.div`
  display: inline-block;
`;

const courseImageStyles = {
  height: '32px',
  width: '32px',
  verticalAlign: 'middle',
  marginRight: '16px',
};

const CourseNameContainer = styled.div`
  flex: 1;
  min-width: 0;
`;

const CourseName = styled.div`
  font-weight: 600;
  color: ${Color.primaryBlack};
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const InstructorNames = styled.div`
  font-size: 0.8em;
  margin-top: 0.2em;
  color: ${Color.reportGray};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CenteredCell = styled.span`
  display: block;
  text-align: center;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: ${Color.darkGray};
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid ${Color.lightGray};
`;

const CreateCopyButton = styled.button`
  background: ${Color.primaryBlue};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;

  &:hover:not(:disabled) {
    background: ${Color.primaryBlue}dd;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
